/* eslint-disable max-len */
import React, { FC, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Box } from '@mui/material';
import SettingsViewComponent from '@features/settings/SettingsViewComponent/SettingsViewComponent';
import { registerReducer } from '../../../../app/store';
import AccessDeniedComponent from '../../auth/AccessDeniedComponent/AccessDeniedComponent';
import ProtectedRoute from '../../auth/ProtectedRoute/ProtectedRoute';
import LoginComponent from '../../auth/LoginComponent/LoginComponent';

import {
    BoddLimitedRole,
    BoddRole,
    MerchantAdminRole,
    MerchantOwnerRole,
    MerchantRole,
    QualityAssuranceRole,
    SystemOwnerRole,
} from '../../auth/auth-constants';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import RegionWrapperComponent from '../RegionWrapperComponent/RegionWrapperComponent';
import LogoutButton from '../../auth/LogoutButton/LogoutButton';

const ProductListViewComponent = React.lazy(() => import('../../../products/ProductListViewComponent/ProductListViewComponent'));
const ScannerListViewComponent = React.lazy(() => import('../../../inventory/ScannerListViewComponent/ScannerListViewComponent'));
const MerchantListViewComponent = React.lazy(() => import('../../../merchants/MerchantListViewComponent/MerchantListViewComponent'));
const MerchantDetailsViewComponent = React.lazy(() => import('../../../merchants/MerchantDetailsViewComponent/MerchantDetailsViewComponent'));
const ScannerDetailViewComponent = React.lazy(() => import('../../../inventory/ScannerDetailViewComponent/ScannerDetailViewComponent'));
const ProductDetailsViewComponent = React.lazy(() => import('../../../products/ProductDetailsViewComponent/ProductDetailsViewComponent'));
const LinkScannerViewComponent = React.lazy(() => import('../../../inventory/LinkScannerViewComponent/LinkScannerViewComponent'));
const ScannerSessionListViewComponent = React.lazy(() => import('../../../scans/ScannerSessionListViewComponent/ScannerSessionListViewComponent'));
const SessionHeaderViewComponent = React.lazy(() => import('../../../scans/session-details/SessionHeaderViewComponent/SessionHeaderViewComponent'));
const ReportsViewComponent = React.lazy(() => import('../../../reports/ReportsViewComponent/ReportsViewComponent'));
const ConfigurationProfileDetailViewComponent = React.lazy(() => import('../../../inventory/configuration/ConfigurationProfileDetailViewComponent/ConfigurationProfileDetailViewComponent'));
const ConfigurationProfileListViewComponent = React.lazy(() => import('../../../inventory/configuration/ConfigurationProfileListViewComponent/ConfigurationProfileListViewComponent'));
const CohortListViewComponent = React.lazy(() => import('../../../cohorts/CohortListViewComponent/CohortListViewComponent'));
const CohortDetailsViewComponent = React.lazy(() => import('../../../cohorts/CohortDetailsViewComponent/CohortDetailsViewComponent'));
const CohortMemberViewComponent = React.lazy(() => import('../../../cohorts/CohortMemberViewComponent/CohortMemberViewComponent'));

interface RoutingComponentProps {
}

const protectedMerchantsComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><MerchantListViewComponent /></Suspense>}
        roles={[BoddRole, SystemOwnerRole]}
        path="/merchants"
    />
);

const protectedMerchantsViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense fallback={<LoadingComponent isLoading />}>
                <MerchantDetailsViewComponent
                    merchantMode={false}
                />
            </Suspense>
        )}
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/merchants/:merchantId"
    />
);

const protectedAccountViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense fallback={<LoadingComponent isLoading />}>
                <MerchantDetailsViewComponent
                    merchantMode
                />
            </Suspense>
        )}
        roles={[MerchantRole, MerchantAdminRole, MerchantOwnerRole, BoddLimitedRole]}
        path="/account"
    />
);

const protectedScanListComponent = (
    <ProtectedRoute
        path="/scans"
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole, QualityAssuranceRole, MerchantRole, MerchantAdminRole, MerchantOwnerRole]}
        component={() => (
            <Suspense
                fallback={<LoadingComponent isLoading />}
            >
                <ScannerSessionListViewComponent />
            </Suspense>
        )}
    />
);

const protectedSessionDetailViewComponent = (
    <ProtectedRoute
        path="/scans/:scanId"
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole, QualityAssuranceRole, MerchantRole, MerchantAdminRole, MerchantOwnerRole]}
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><SessionHeaderViewComponent /></Suspense>}
    />
);

const protectedProductComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><ProductDetailsViewComponent /></Suspense>}
        roles={[BoddLimitedRole, MerchantRole, MerchantAdminRole, MerchantOwnerRole]}
        path="/products/:productCode"
    />
);

const protectedProductListComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><ProductListViewComponent /></Suspense>}
        roles={[BoddLimitedRole, MerchantRole, MerchantAdminRole, MerchantOwnerRole]}
        path="/products"
        requiredConfigurationKey="sizeMatchEnabled"
    />
);

const protectedScannersListComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><ScannerListViewComponent /></Suspense>}
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole, QualityAssuranceRole, MerchantAdminRole, MerchantOwnerRole]}
        path="/scanners"
    />
);

const protectedScannerDetailViewComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><ScannerDetailViewComponent /></Suspense>}
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole, MerchantAdminRole, MerchantOwnerRole]}
        path="/scanners/:scannerId"
    />
);

const protectedConfigurationListViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense
                fallback={<LoadingComponent isLoading />}
            >
                <ConfigurationProfileListViewComponent />
            </Suspense>
        )}
        roles={[MerchantRole, MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/profiles/:profileId"
    />
);

const protectedConfigurationDetailViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense
                fallback={<LoadingComponent isLoading />}
            >
                <ConfigurationProfileDetailViewComponent />
            </Suspense>
        )}
        roles={[MerchantRole, MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/profiles/:profileId"
    />
);

const protectedLinkScannerViewComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><LinkScannerViewComponent /></Suspense>}
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole, MerchantAdminRole, MerchantOwnerRole]}
        path="/link-scanner"
    />
);

const protectedReportsViewComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><ReportsViewComponent /></Suspense>}
        roles={[MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/reports"
    />
);

const protectedCohortsViewComponent = (
    <ProtectedRoute
        component={() => <Suspense fallback={<LoadingComponent isLoading />}><CohortListViewComponent /></Suspense>}
        roles={[MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/cohorts"
    />
);

const protectedCohortDetailsViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense fallback={<LoadingComponent isLoading />}>
                <CohortDetailsViewComponent />
            </Suspense>
        )}
        roles={[MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/cohorts/:cohortId"
    />
);

const protectedCohortMemberViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense fallback={<LoadingComponent isLoading />}>
                <CohortMemberViewComponent />
            </Suspense>
        )}
        roles={[MerchantAdminRole, MerchantOwnerRole, BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/cohortmembers/:cohortMemberId"
    />
);

const protectedSettingsViewComponent = (
    <ProtectedRoute
        component={() => (
            <Suspense fallback={<LoadingComponent isLoading />}>
                <SettingsViewComponent />
            </Suspense>
        )}
        roles={[BoddRole, BoddLimitedRole, SystemOwnerRole]}
        path="/system-settings"
    />
);

const fallbackComponent = () => <h1>Something went wrong.</h1>;

const PrependRegion = () => {
    const p = useParams();
    const original = p['*'];

    return <Navigate to={`/au/${original}`} replace />;
};

const RoutingComponent: FC<RoutingComponentProps> = () => {
    useEffect(() => {
        Promise.all([
            import('../../../merchants/merchantsSlice'),
            import('../../../scans/scanSlice'),
            import('../../../products/productsSlice'),
            import('../../../inventory/inventorySlice'),
        ]).then(([merchants, scans, products, inventory]) => {
            registerReducer('merchants', merchants.default);
            registerReducer('scans', scans.default);
            registerReducer('products', products.default);
            registerReducer('inventory', inventory.default);
        });
    }, []);

    return (
        <Suspense fallback={<div data-testid="RoutingComponent">Loading</div>}>
        <Sentry.ErrorBoundary fallback={fallbackComponent}>
            <Routes>
                <Route path="/scans/:any" element={<PrependRegion />} />
                <Route path="/reports" element={<PrependRegion />} />
                <Route path="/user-logout" element={<Box><LogoutButton /></Box>} />
                <Route
                    path="/:region"
                    element={<RegionWrapperComponent />}
                >
                    <Route
                        path="scans"
                        element={protectedScanListComponent}
                    />
                    <Route
                        path="scans/:sessionId"
                        element={protectedSessionDetailViewComponent}
                    />
                    <Route
                        path="reports"
                        element={protectedReportsViewComponent}
                    />
                    <Route path="" element={<Navigate to="scans" replace />} />
                </Route>
                <Route
                    path="/merchants/:merchantId"
                    element={protectedMerchantsViewComponent}
                />
                <Route
                    path="/merchants"
                    element={protectedMerchantsComponent}
                />
                <Route
                    path="/products"
                    element={protectedProductListComponent}
                />
                <Route
                    path="/products/:productCode"
                    element={protectedProductComponent}
                />
                <Route
                    path="/scanners"
                    element={protectedScannersListComponent}
                />
                <Route
                    path="/scanners/link"
                    element={protectedLinkScannerViewComponent}
                />
                <Route
                    path="/scanners/:deviceId"
                    element={protectedScannerDetailViewComponent}
                />
                <Route
                    path="/profiles"
                    element={protectedConfigurationListViewComponent}
                />
                <Route
                    path="/profiles/:profileIdParam"
                    element={protectedConfigurationDetailViewComponent}
                />
                <Route
                    path="/cohorts"
                    element={protectedCohortsViewComponent}
                />
                <Route
                    path="/cohorts/:cohortId"
                    element={protectedCohortDetailsViewComponent}
                />
                <Route
                    path="/cohortMembers/:cohortMemberId"
                    element={protectedCohortMemberViewComponent}
                />
                <Route path="/system-settings" element={protectedSettingsViewComponent} />
                <Route path="/account" element={protectedAccountViewComponent} />
                <Route path="/login" element={<LoginComponent />} />
                <Route path="access-denied" element={<AccessDeniedComponent />} />
                {/*
                        Return an empty component, preventing the wildcard "*" route from handling the "/" path
                        since the wildcard navigates to "/" causing an infinite loop.
                        The "/" path should be handled by a route higher in the component hierarchy so this
                        is just a fail-safe.
                    */}
                <Route path="/" element={null} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Sentry.ErrorBoundary>
        </Suspense>
    );
};

export default RoutingComponent;
